"use client";

import {
  ArrowLeft,
  Check,
  ChevronRight,
  Eye,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { api } from "@/spa/api";
import {
  CONNECTION_TYPES,
  POSTER_TYPES,
  SEEKING_TYPES,
} from "@/data/post-taxonomy";
import { useSearchLocation } from "./location-context";

type Mode = "post" | "date";
type Draft = {
  intention: string;
  details: string;
  audience: string;
  category: string;
  posterType: string;
  seeking: string[];
  location: string;
  privacy: string;
  when: string;
};

const initial: Draft = {
  intention: "",
  details: "",
  audience: "Singles and couples",
  category: "",
  posterType: "",
  seeking: [],
  location: "Brisbane City",
  privacy: "Suburb only",
  when: "",
};

export function CreateFlow({ mode }: { mode: Mode }) {
  const searchLocation = useSearchLocation();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft>(initial);
  const [published, setPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const key = `krosspaths-${mode}-draft`;
  const steps = useMemo(
    () =>
      mode === "post"
        ? ["Connection", "Details", "Who", "Location", "Photos", "Preview"]
        : ["Plan", "When", "Where", "Audience", "Privacy", "Preview"],
    [mode],
  );

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const stored = localStorage.getItem(key);
      if (stored) setDraft({ ...initial, ...JSON.parse(stored) });
    });
    return () => cancelAnimationFrame(frame);
  }, [key]);

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(draft));
  }, [draft, key]);
  const update = (field: keyof Draft, value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));
  const valid =
    mode === "date"
      ? step !== 0 || draft.intention.trim().length >= 5
      : step === 0
        ? draft.intention.trim().length >= 5 && Boolean(draft.category)
        : step === 1
          ? draft.details.trim().length >= 20
          : step === 2
            ? Boolean(draft.posterType) && draft.seeking.length > 0
            : true;

  async function publish() {
    setPublishing(true);
    setError("");
    try {
      const coordinates = {
        latitude: searchLocation.latitude,
        longitude: searchLocation.longitude,
      };
      if (mode === "post")
        await api.post("/posts", {
          title: draft.intention,
          description: draft.details,
          category: draft.category,
          poster_type: draft.posterType,
          seeking: draft.seeking,
          location_label: draft.location,
          location_privacy: privacyValue(draft.privacy),
          ...coordinates,
          publish: true,
        });
      else
        await api.post("/dates", {
          title: draft.intention,
          description: draft.details,
          starts_at: draft.when,
          timezone: "Australia/Brisbane",
          location_label: draft.location,
          location_privacy: privacyValue(draft.privacy, true),
          ...coordinates,
          capacity: 4,
          seeking: [draft.audience],
          attendance_rules: { location_shared_after_acceptance: true },
          publish: true,
        });
      localStorage.removeItem(key);
      setPublished(true);
    } catch (value) {
      const message =
        typeof value === "object" && value && "response" in value
          ? (value as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : null;
      setError(
        message ??
          "This could not be published. Check your connection and account status.",
      );
    } finally {
      setPublishing(false);
    }
  }

  if (published)
    return (
      <div className="success-state">
        <div>
          <div className="success-mark">
            <Check size={38} />
          </div>
          <p className="eyebrow">Published</p>
          <h1 className="page-title">
            Your {mode === "post" ? "Post" : "Date"} is live.
          </h1>
          <p className="muted">
            It’s visible around {draft.location} using your selected privacy
            setting.
          </p>
          <Link className="button button-primary" to="/">
            Return to Discover
          </Link>
        </div>
      </div>
    );

  return (
    <div className="create-flow">
      <header className="flow-top">
        <Link className="icon-button" to="/" aria-label="Exit creation">
          <ArrowLeft size={19} />
        </Link>
        <div className="progress-track">
          <div
            className="progress-value"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>
        <span className="muted">
          {step + 1}/{steps.length}
        </span>
      </header>
      <main className="flow-body">
        <p className="eyebrow">
          Create {mode === "post" ? "a Post" : "a Date"} · {steps[step]}
        </p>
        {step === 0 && (
          <>
            <h1>
              {mode === "post"
                ? "What are you looking for?"
                : "What is the plan?"}
            </h1>
            <p className="muted">
              Lead with a clear, direct intention. You can fine-tune it before
              publishing.
            </p>
            <div className="field">
              <label htmlFor="intention">Title</label>
              <input
                id="intention"
                value={draft.intention}
                onChange={(e) => update("intention", e.target.value)}
                placeholder={
                  mode === "post"
                    ? "Drinks and conversation tonight"
                    : "A quiet rooftop drink"
                }
                maxLength={100}
              />
              <small className="muted">
                {draft.intention.length}/100 · saved automatically
              </small>
            </div>
            {mode === "post" && (
              <fieldset className="taxonomy-group">
                <legend>What kind of connection?</legend>
                <p className="muted">Choose one. This is the Post category people browse.</p>
                <div className="choice-grid compact-choices">
                  {CONNECTION_TYPES.map((option) => (
                    <button
                      className={`choice ${draft.category === option ? "selected" : ""}`}
                      type="button"
                      key={option}
                      aria-pressed={draft.category === option}
                      onClick={() => update("category", option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
            {mode === "date" && (
              <div className="field">
                <label htmlFor="date-details">Plan details</label>
                <textarea
                  id="date-details"
                  value={draft.details}
                  onChange={(e) => update("details", e.target.value)}
                  placeholder="What should people expect?"
                />
              </div>
            )}
          </>
        )}
        {step === 1 && mode === "post" && (
          <>
            <h1>Tell people a little more.</h1>
            <p className="muted">
              Set expectations without sharing private contact or address
              details.
            </p>
            <div className="field">
              <label htmlFor="details">Description</label>
              <textarea
                id="details"
                value={draft.details}
                onChange={(e) => update("details", e.target.value)}
                placeholder="What would make this a good meetup?"
              />
            </div>
          </>
        )}
        {step === 1 && mode === "date" && (
          <>
            <h1>When should people arrive?</h1>
            <div className="field">
              <label htmlFor="when">Date and start time</label>
              <input
                id="when"
                type="datetime-local"
                value={draft.when}
                onChange={(e) => update("when", e.target.value)}
              />
            </div>
            <div className="privacy-preview">
              <strong>Timezone: Australia/Brisbane</strong>
              <p className="muted">
                Attendees see the time in their local timezone.
              </p>
            </div>
          </>
        )}
        {step === 2 && mode === "post" && (
          <PostAudience draft={draft} setDraft={setDraft} />
        )}
        {step === 2 && mode === "date" && (
          <Location draft={draft} update={update} />
        )}
        {step === 3 && mode === "post" && (
          <Location draft={draft} update={update} />
        )}
        {step === 3 && mode === "date" && (
          <Audience draft={draft} update={update} />
        )}
        {step === 4 && mode === "post" && (
          <>
            <h1>Add photos or a private album.</h1>
            <p className="muted">
              Optional. Private media stays locked until you grant access.
            </p>
            <button className="choice" type="button">
              <Eye size={20} />
              <strong style={{ display: "block", marginTop: ".5rem" }}>
                Choose from albums
              </strong>
              <span className="muted">Public or controlled access</span>
            </button>
          </>
        )}
        {step === 4 && mode === "date" && (
          <>
            <h1>Privacy and attendance.</h1>
            <div className="privacy-preview">
              <ShieldCheck />
              <strong style={{ display: "block" }}>
                Location shared after acceptance
              </strong>
              <p className="muted">
                Applicants see only {draft.location}. Accepted guests receive
                the final meeting point when you release it.
              </p>
            </div>
          </>
        )}
        {step === 5 && (
          <>
            <h1>Ready to publish?</h1>
            <p className="muted">
              This is how your {mode === "post" ? "Post" : "Date"} will appear
              nearby.
            </p>
            <article className="publish-preview">
              <span
                className={`tag ${mode === "date" ? "tag-fresh" : "tag-warm"}`}
              >
                {mode === "post" ? draft.category : "Open Date"}
              </span>
              <h2 className="post-title">
                {draft.intention || `Untitled ${mode}`}
              </h2>
              <p>
                {draft.details ||
                  (mode === "date"
                    ? "A scheduled plan with attendance managed by you."
                    : "Add a little more detail before publishing.")}
              </p>
              <div className="meta-row">
                <span>
                  <MapPin size={13} /> {draft.location}
                </span>
                <span>{draft.privacy}</span>
              </div>
              {mode === "post" && (
                <p className="audience-summary">
                  {draft.posterType} looking for {draft.seeking.join(", ")}
                </p>
              )}
            </article>
          </>
        )}
      </main>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <footer className="flow-footer">
        <button
          className="button button-quiet"
          type="button"
          disabled={step === 0 || publishing}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          Back
        </button>
        {step < steps.length - 1 ? (
          <button
            className="button button-primary"
            type="button"
            disabled={!valid}
            onClick={() => setStep((s) => s + 1)}
          >
            Continue <ChevronRight size={18} />
          </button>
        ) : (
          <button
            className="button button-primary"
            type="button"
            disabled={publishing}
            onClick={publish}
          >
            {publishing
              ? "Publishing…"
              : `Publish ${mode === "post" ? "Post" : "Date"}`}
          </button>
        )}
      </footer>
    </div>
  );
}

function Audience({
  draft,
  update,
}: {
  draft: Draft;
  update: (field: keyof Draft, value: string) => void;
}) {
  const options = [
    "Singles",
    "Couples",
    "Singles and couples",
    "Established members",
  ];
  return (
    <>
      <h1>Who do you want to meet?</h1>
      <p className="muted">
        This controls discovery and who can contact you from this listing.
      </p>
      <div className="choice-grid">
        {options.map((option) => (
          <button
            className={`choice ${draft.audience === option ? "selected" : ""}`}
            type="button"
            key={option}
            onClick={() => update("audience", option)}
          >
            {option}
          </button>
        ))}
      </div>
    </>
  );
}

function PostAudience({
  draft,
  setDraft,
}: {
  draft: Draft;
  setDraft: Dispatch<SetStateAction<Draft>>;
}) {
  const toggleSeeking = (option: string) =>
    setDraft((current) => {
      if (option === "Any") return { ...current, seeking: ["Any"] };
      const withoutAny = current.seeking.filter((item) => item !== "Any");
      return {
        ...current,
        seeking: withoutAny.includes(option)
          ? withoutAny.filter((item) => item !== option)
          : [...withoutAny, option],
      };
    });

  return (
    <>
      <h1>Who is this Post for?</h1>
      <p className="muted">
        Pick who is posting and one or more people you would like to meet.
      </p>
      <fieldset className="taxonomy-group">
        <legend>Posting as</legend>
        <div className="choice-grid compact-choices">
          {POSTER_TYPES.map((option) => (
            <button
              className={`choice ${draft.posterType === option ? "selected" : ""}`}
              type="button"
              key={option}
              aria-pressed={draft.posterType === option}
              onClick={() => setDraft((current) => ({ ...current, posterType: option }))}
            >
              {option}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="taxonomy-group">
        <legend>Looking for</legend>
        <p className="muted">Choose more than one if you like.</p>
        <div className="choice-grid compact-choices">
          {SEEKING_TYPES.map((option) => (
            <button
              className={`choice ${draft.seeking.includes(option) ? "selected" : ""}`}
              type="button"
              key={option}
              aria-pressed={draft.seeking.includes(option)}
              onClick={() => toggleSeeking(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </fieldset>
    </>
  );
}

function Location({
  draft,
  update,
}: {
  draft: Draft;
  update: (field: keyof Draft, value: string) => void;
}) {
  return (
    <>
      <h1>Where should it appear?</h1>
      <div className="field">
        <label htmlFor="location">General location</label>
        <input
          id="location"
          value={draft.location}
          onChange={(e) => update("location", e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="privacy">What others see</label>
        <select
          id="privacy"
          value={draft.privacy}
          onChange={(e) => update("privacy", e.target.value)}
        >
          <option>Suburb only</option>
          <option>Approximate distance</option>
          <option>Exact venue</option>
          <option>Hidden until shared in chat</option>
        </select>
      </div>
      <div className="privacy-preview">
        <ShieldCheck />
        <strong style={{ display: "block" }}>
          Your home address remains private.
        </strong>
        <p className="muted">
          People will see “{draft.location}” and the setting “{draft.privacy}”.
        </p>
      </div>
    </>
  );
}

function privacyValue(label: string, date = false) {
  const values: Record<string, string> = {
    "Suburb only": "suburb",
    "Approximate distance": "approximate",
    "Exact venue": "venue",
    "Hidden until shared in chat": "hidden",
  };
  return values[label] ?? (date ? "hidden" : "suburb");
}
